<template>
    <div class="bg-white rounded-3xl">
        <div class="bg-amazon-200 p-4 rounded-t-3xl rounded-b-lg">
            <div class="flex justify-between items-center">
                <UButton variant="link" icon="i-solar-camera-minimalistic-linear" size="xl" :padded="false" />
                <UButton variant="link" icon="i-solar-pen-linear" :padded="false" to="/dashboard/me" />
            </div>
            <div class="flex flex-col justify-center items-center">
                <UAvatar :src="profileImage" :alt="user?.data.name?.toUpperCase()" size="3xl" />
                <UiComponentTypography class-name="mt-2">
                    {{ user?.data.name }}
                </UiComponentTypography>
                <UiComponentTypography variant="subtitle2">
                    {{ user?.data.phone }}
                </UiComponentTypography>
            </div>
        </div>
        <div class="p-4 rounded-b-3xl">
            <ul class="menuLinks">
                <li>
                    <div class="flex justify-between items-center text-sm text-gray-500">
                        <UiComponentTypography class-name="flex items-center gap-2">
                            <UIcon name="i-solar-dollar-minimalistic-line-duotone" class="w-5 h-5" />
                            اعتبار حساب شما:
                        </UiComponentTypography>
                        <UiComponentTypography>
                            {{ commaNumber(accountBalance, { currencySymbol: "تومان" }) }}
                        </UiComponentTypography>
                    </div>
                </li>
                <li class="my-4">
                    <UDivider />
                </li>
                <li v-for="(item, index) in links" class="mt-4 first:mt-0">
                    <NuxtLink
                        class="flex items-center gap-4 group relative menuItemLink"
                        :class="
                            index === activeMenuIndex
                                ? `bg-${item.color}-100 rounded-md`
                                : `before:bg-${item.color}-100`
                        "
                        :to="item.link"
                    >
                        <div
                            class="w-10 h-10 p-2 rounded-md"
                            :class="
                                index === activeMenuIndex
                                    ? `bg-${item.color}-500 text-white`
                                    : `bg-${item.color}-100 text-${item.color}-500`
                            "
                        >
                            <UIcon :name="item.icon" class="w-full h-full" />
                        </div>
                        <UiComponentTypography
                            tag="span"
                            :class-name="
                                index === activeMenuIndex
                                    ? `relative text-${item.color}-800`
                                    : `relative group-hover:text-${item.color}-800`
                            "
                        >
                            {{ item.label }}
                        </UiComponentTypography>
                    </NuxtLink>
                </li>
                <li class="my-4">
                    <UDivider />
                </li>
                <li>
                    <NuxtLink
                        class="flex items-center gap-4 group relative before:bg-rose-100 menuItemLink cursor-pointer"
                        @click="openLogout = true"
                    >
                        <div class="w-10 h-10 p-2 rounded-md bg-rose-100 text-rose-500">
                            <UIcon name="i-solar-logout-2-line-duotone" class="w-full h-full" />
                        </div>
                        <UiComponentTypography tag="span" class-name="relative group-hover:text-rose-800">
                            خروج
                        </UiComponentTypography>
                    </NuxtLink>
                    <UModal v-model="openLogout" :ui="{ width: 'w-full sm:max-w-64' }">
                        <div class="bg-white p-8 rounded">
                            <UiComponentTypography variant="subtitle" class-name="text-center">
                                آیا می‌خواهید از حساب کاربری خود خارج شوید؟
                            </UiComponentTypography>
                            <div class="flex items-center justify-between gap-4 mt-6">
                                <div class="w-1/2">
                                    <UButton color="amazon" @click="openLogout = false" block>انصراف</UButton>
                                </div>
                                <div class="w-1/2">
                                    <UButton color="rose" variant="outline" block @click="logout()">خروج</UButton>
                                </div>
                            </div>
                        </div>
                    </UModal>
                </li>
            </ul>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { IUser } from "~/libs/types/api/user";
import type { IResponse } from "~/libs/types/api/pagination";

const user = useSanctumUser<IResponse<IUser>>();

const route = useRoute();

const { logout } = useSanctumAuth();

const openLogout = ref(false);

const profileImage = computed(() => {
    return getImageUrl(user.value?.data.profile?.avatar, "avatar");
});

const links = [
    {
        color: "vista",
        icon: "i-solar-cart-check-line-duotone",
        label: "سفارشات من",
        link: "/dashboard/orders",
    },
    {
        color: "amber",
        icon: "i-solar-bookmark-line-duotone",
        label: "علاقه‌مندی‌ها",
        link: "/dashboard/bookmarks",
    },
    {
        color: "sky",
        icon: "i-solar-point-on-map-line-duotone",
        label: "آدرس‌ها",
        link: "/dashboard/addresses",
    },
    {
        color: "teal",
        icon: "i-solar-wallet-money-line-duotone",
        label: "کیف پول من",
        link: "/dashboard/wallet",
    },
    {
        color: "amazon",
        icon: "i-solar-user-circle-line-duotone",
        label: "اطلاعات حساب",
        link: "/dashboard/me",
    },
];

const activeMenuIndex = computed(() => {
    switch (route.path) {
        case "/dashboard/orders":
            return 0;
        case "/dashboard/bookmarks":
            return 1;
        case "/dashboard/addresses":
            return 2;
        case "/dashboard/wallet":
            return 3;
        case "/dashboard/me":
            return 4;
        default:
            break;
    }
});

const accountBalance = computed(() => {
    return (user.value?.data?.wallet?.balance ?? 0) + (user.value?.data?.wallet?.credit ?? 0);
});

/* class
    bg-vista-100 before:bg-vista-100 bg-vista-100 bg-vista-500 text-vista-500 text-vista-800 group-hover:text-vista-800
    bg-amber-100 before:bg-amber-100 bg-amber-100 bg-amber-500 text-amber-500 text-amber-800 group-hover:text-amber-800
    bg-sky-100 before:bg-sky-100 bg-sky-100 bg-sky-500 text-sky-500 text-sky-800 group-hover:text-sky-800
    bg-teal-100 before:bg-teal-100 bg-teal-100 bg-teal-500 text-teal-500 text-teal-800 group-hover:text-teal-800
    bg-amazon-100 before:bg-amazon-100 bg-amazon-100 bg-amazon-500 text-amazon-500 text-amazon-800 group-hover:text-amazon-800
*/
</script>

<style scoped>
.menuItemLink::before {
    content: "";
    @apply absolute w-0 h-full top-0 right-0 rounded-md transition-all duration-300 ease-in-out;
}
.menuItemLink:hover::before {
    @apply w-full;
}
</style>
